import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

import { Plus, Search, Loader2, ShieldAlert, ShieldCheck, Info, Download, Sheet } from 'lucide-react';
import { toCSV, downloadCSV, openInGoogleSheets } from '../../../utils/csvExport';
import { getAllSafetyRules, saveSafetyRule } from '../../../services/firestore/formularyService';
import type { SafetyRule, SafetyRuleSeverity } from '../../../types/formulary';

const SEVERITY_CONFIG: Record<SafetyRuleSeverity, { label: string; color: string; bgColor: string; borderColor: string; icon: typeof ShieldAlert }> = {
  block: { label: 'Block', color: 'text-critical', bgColor: 'bg-red-50', borderColor: 'border-red-200', icon: ShieldAlert },
  warn: { label: 'Warn', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', icon: ShieldCheck },
  info: { label: 'Info', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', icon: Info },
};

export function SafetyRuleList() {
  const navigate = useNavigate();
  const [rules, setRules] = useState<SafetyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterSeverity, setFilterSeverity] = useState<SafetyRuleSeverity | 'all'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    try {
      setLoading(true);
      const data = await getAllSafetyRules();
      setRules(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load safety rules');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return rules.filter((r) => {
      if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.message.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterSeverity !== 'all' && r.severity !== filterSeverity) return false;
      if (filterActive === 'active' && !r.isActive) return false;
      if (filterActive === 'inactive' && r.isActive) return false;
      return true;
    });
  }, [rules, search, filterSeverity, filterActive]);

  async function toggleActive(rule: SafetyRule) {
    const updated = { ...rule, isActive: !rule.isActive };
    try {
      await saveSafetyRule(updated);
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
    } catch (err) {
      alert('Failed to update: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        <p className="font-medium">Error loading safety rules</p>
        <p className="text-base mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-display text-gold">Safety Rules</h2>
          <p className="text-base text-text-muted">{rules.length} total, {filtered.length} shown</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((r) => ({
                Name: r.name, Severity: r.severity, 'Trigger Type': r.triggerType,
                Active: r.isActive, Message: r.message,
                'Clinical Note': r.clinicalNote,
                'Match Medications': r.matchMedications || [],
                'Match Conditions': r.matchConditions || [],
                'Match Allergens': r.matchAllergens || [],
                'Affected Products': r.affectedProductIds,
              })), [
                { key: 'Name', label: 'Name' }, { key: 'Severity', label: 'Severity' },
                { key: 'Trigger Type', label: 'Trigger Type' }, { key: 'Active', label: 'Active' },
                { key: 'Message', label: 'Message' }, { key: 'Clinical Note', label: 'Clinical Note' },
                { key: 'Match Medications', label: 'Match Medications' },
                { key: 'Match Conditions', label: 'Match Conditions' },
                { key: 'Match Allergens', label: 'Match Allergens' },
                { key: 'Affected Products', label: 'Affected Product IDs' },
              ]);
              downloadCSV(csv, 'hemologix-safety-rules.csv');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((r) => ({
                Name: r.name, Severity: r.severity, 'Trigger Type': r.triggerType,
                Active: r.isActive, Message: r.message,
                'Match Medications': r.matchMedications || [],
                'Affected Products': r.affectedProductIds,
              })), [
                { key: 'Name', label: 'Name' }, { key: 'Severity', label: 'Severity' },
                { key: 'Trigger Type', label: 'Trigger Type' }, { key: 'Active', label: 'Active' },
                { key: 'Message', label: 'Message' },
                { key: 'Match Medications', label: 'Match Medications' },
                { key: 'Affected Products', label: 'Affected Product IDs' },
              ]);
              openInGoogleSheets(csv, 'Hemologix Safety Rules');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
          >
            <Sheet size={16} className="text-optimal" />
            Sheets
          </button>
          <Link
            to="/admin/safety/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors"
          >
            <Plus size={16} />
            Add Rule
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold/50"
            />
          </div>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value as SafetyRuleSeverity | 'all')}
            className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary focus:outline-none focus:border-gold/50"
          >
            <option value="all">All Severities</option>
            <option value="block">Block</option>
            <option value="warn">Warn</option>
            <option value="info">Info</option>
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-primary focus:outline-none focus:border-gold/50"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Rules list */}
      <div className="space-y-3">
        {filtered.map((rule, i) => {
          const sev = SEVERITY_CONFIG[rule.severity];
          return (
            <motion.div
              key={rule.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              onClick={() => navigate(`/admin/safety/${rule.id}`)}
              className="bg-bg-card border border-bg-tertiary rounded-xl p-4 hover:border-gold/20 cursor-pointer transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${sev.bgColor} flex-shrink-0`}>
                  <sev.icon size={16} className={sev.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-medium text-text-primary truncate">{rule.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium border ${sev.bgColor} ${sev.borderColor} ${sev.color}`}>
                      {sev.label}
                    </span>
                    <span className="text-sm text-text-muted capitalize">{rule.triggerType.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-2">{rule.message}</p>
                  {rule.affectedProductIds.length > 0 && (
                    <p className="text-sm text-text-muted mt-1">
                      Affects {rule.affectedProductIds.length} product{rule.affectedProductIds.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleActive(rule);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    rule.isActive
                      ? 'border-green-200 text-optimal bg-green-50 hover:bg-green-100'
                      : 'border-bg-tertiary text-text-muted bg-bg-elevated hover:bg-bg-tertiary'
                  }`}
                >
                  {rule.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-text-muted bg-bg-card border border-bg-tertiary rounded-xl">
          <p>No safety rules match your filters.</p>
        </div>
      )}
    </div>
  );
}
